import { createTheme } from "@mui/material/styles";

const mainTheme = createTheme({
  palette: {
    primary: {
      main: "#603F97",
      light: "#9575CD",
      dark: "#4F337A",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#D31D83",
      light: "#F8D7E9",
      dark: "#B0186F",
      contrastText: "#ffffff",
    },
    error: {
      main: "#E53935",
      light: "#FF6F61",
      dark: "#B71C1C",
      contrastText: "#ffffff",
    },
    warning: {
      main: "#ED6C02",
      light: "#FF9800",
      dark: "#E65100",
      contrastText: "#ffffff",
    },
    info: {
      main: "#0288D1",
      light: "#03A9F4",
      dark: "#01579B",
      contrastText: "#ffffff",
    },
    success: {
      main: "#2E7D32",
      light: "#4CAF50",
      dark: "#1B5E20",
      contrastText: "#ffffff",
    },
    background: {
      paper: "#FFF",
      default: "#FFF",
    },
    text: {
      primary: "#333333",
      secondary: "#666666",
      disabled: "#999999",
      hint: "#888888",
    },
    action: {
      active: "rgba(96, 63, 151, 0.54)",
      hover: "rgba(96, 63, 151, 0.04)",
      selected: "rgba(96, 63, 151, 0.08)",
      disabled: "rgba(96, 63, 151, 0.26)",
      disabledBackground: "rgba(96, 63, 151, 0.12)",
      focus: "rgba(96, 63, 151, 0.12)",
    },
  },
  typography: {
    fontFamily: '"Montserrat", "Inter", "Arial", sans-serif',
    button: {
      fontFamily: '"Inter", "Arial", sans-serif',
      fontWeight: 500,
      textTransform: "uppercase",
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        contained: {
          backgroundColor: "#603F97",
          color: "#ffffff",
          "&:hover": {
            backgroundColor: "#9575CD",
            boxShadow: "0px 0px 0px 4px rgba(96, 63, 151, 0.2)",
          },
          "&:disabled": {
            backgroundColor: "#603F97",
            opacity: 0.6,
            color: "#999999",
          },
        },
        outlined: {
          borderColor: "#603F97",
          color: "#603F97",
          "&:hover": {
            borderColor: "#4F337A",
            backgroundColor: "rgba(96, 63, 151, 0.1)",
          },
          "&:disabled": {
            borderColor: "#999999",
            color: "#999999",
          },
        },
        text: {
          color: "#603F97",
          "&:hover": {
            backgroundColor: "rgba(96, 63, 151, 0.04)",
          },
          "&:disabled": {
            color: "#999999",
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            transform: "scale(1.1)",
          },
        },
        colorPrimary: {
          "&:hover": {
            backgroundColor: "primary.light",
            color: "primary.main",
          },
        },
        colorSecondary: {
          "&:hover": {
            backgroundColor: "secondary.light",
            color: "secondary.main",
          },
        },
        colorError: {
          "&:hover": {
            backgroundColor: "error.light",
            color: "error.main",
          },
        },
        colorSuccess: {
          "&:hover": {
            backgroundColor: "success.light",
            color: "success.main",
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          "& .MuiSvgIcon-root": {
            backgroundColor: "background.paper",
            borderRadius: "4px",
            border: "2px solid",
            borderColor: "text.secondary",
            transition:
              "border-color 0.2s ease-in-out, background-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
          },
          "&.Mui-checked .MuiSvgIcon-root": {
            borderColor: "primary.main",
            backgroundColor: "primary.light",
            color: "primary.main",
            boxShadow: "0px 0px 0px 4px rgba(96, 63, 151, 0.1)",
          },
          "&:hover .MuiSvgIcon-root": {
            borderColor: "primary.main",
            backgroundColor: "primary.light",
            boxShadow: "0px 0px 0px 4px rgba(96, 63, 151, 0.1)",
          },
          "&.Mui-checked:hover .MuiSvgIcon-root": {
            borderColor: "primary.dark",
            backgroundColor: "#D6CCEB",
            boxShadow: "0px 0px 0px 6px rgba(96, 63, 151, 0.15)",
          },
          "& .MuiSvgIcon-root.Mui-focusVisible": {
            borderColor: "primary.main",
            boxShadow: "0px 0px 0px 6px rgba(96, 63, 151, 0.2)",
            outline: "none",
          },
          "&.Mui-disabled .MuiSvgIcon-root": {
            backgroundColor: "background.default",
            borderColor: "text.disabled",
            color: "text.disabled",
            boxShadow: "none",
          },
          "&.Mui-disabled.Mui-checked .MuiSvgIcon-root": {
            backgroundColor: "background.default",
            borderColor: "text.disabled",
            color: "text.disabled",
            opacity: 0.6,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            backgroundColor: "background.paper",
            transition:
              "border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
            "& fieldset": {
              borderColor: "text.secondary",
            },
            "&:hover fieldset": {
              borderColor: "primary.main",
            },
            "&.Mui-focused fieldset": {
              borderColor: "primary.main",
              boxShadow: "0px 0px 0px 4px rgba(96, 63, 151, 0.1)",
            },
            "&.Mui-disabled": {
              backgroundColor: "background.default",
              opacity: 0.6,
              "& fieldset": {
                borderColor: "text.disabled",
              },
            },
          },
          "& .MuiInputLabel-root": {
            color: "text.secondary",
            "&.Mui-focused": {
              color: "primary.main",
            },
            "&.Mui-disabled": {
              color: "text.disabled",
            },
          },
          "& .MuiOutlinedInput-input": {
            "&.Mui-disabled": {
              color: "text.disabled",
              WebkitTextFillColor: "text.disabled",
            },
          },
        },
      },
    },
    MuiTooltip: {
      defaultProps: {
        PopperProps: {
          popperOptions: {
            // Portalled tooltips must not expand the document while scrolling.
            strategy: "fixed",
            modifiers: [
              {
                name: "preventOverflow",
                options: {
                  rootBoundary: "viewport",
                  altAxis: true,
                  padding: 8,
                },
              },
            ],
          },
        },
      },
      styleOverrides: {
        popper: {
          // Popper marks references clipped by a scrolling ancestor.
          "&[data-popper-reference-hidden]": {
            visibility: "hidden",
            pointerEvents: "none",
          },
        },
        tooltip: {
          backgroundColor: "rgba(33, 33, 33, 0.95)", // 深灰，更柔和
          color: "#FFFFFF",
          fontSize: "0.8125rem", // 13px，更易读
          fontWeight: 500,
          padding: "8px 14px",
          borderRadius: "8px",
          boxShadow:
            "0px 4px 12px rgba(0, 0, 0, 0.15), 0px 1px 2px rgba(0, 0, 0, 0.08)",
          maxWidth: "320px", // 防止过长文本
          lineHeight: 1.4,
          letterSpacing: "0.25px",
          backdropFilter: "blur(4px)", // 毛玻璃效果（可选）
          border: "1px solid rgba(255, 255, 255, 0.08)", // 细边框增强层次
          // 字体优化
          fontFamily:
            '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          // 动画优化
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          // 间距优化
          margin: "4px 0", // 与触发元素的垂直间距
        },
        arrow: {
          color: "rgba(33, 33, 33, 0.95)", // 与 tooltip 一致
          fontSize: "18px", // 稍大箭头，更明显
        },
        tooltipPlacementTop: {
          marginBottom: "6px !important", // 顶部提示增加间距
        },
        tooltipPlacementBottom: {
          marginTop: "6px !important", // 底部提示增加间距
        },
        tooltipPlacementLeft: {
          marginRight: "6px !important",
        },
        tooltipPlacementRight: {
          marginLeft: "6px !important",
        },
      },
    },
  },
});

export default mainTheme;
